/** A drawing held in the browser, answering what `api.ts` answers — phase 3.
 *
 * ⚠⚠ **THIS IS NOT A MOCK OF THE SERVER AND IT IS NOT A SECOND IMPLEMENTATION OF ANYTHING.**
 * Every read here is one call into the same Rust that answers the server's endpoints, and the
 * backend suite already holds the two together endpoint by endpoint
 * (`tests.py`'s store/view pairs: layouts, layers, blocks, blockatts, groups, sysvars,
 * textstyles, fields, dimstyles, text, block_def, xrefs — each compared to the VIEW's own
 * response). So the shapes below are not guesses: they are the same JSON, from the same code.
 *
 * ⚠⚠ **WHAT IS NOT HERE IS SAID OUT LOUD RATHER THAN FAKED.** `grips` needs a row's kind, `data`
 * and extrusion, and the store does not expose a row; `measure`, `parametric`, the raster
 * attachments and the import path all belong to the server today. Calling one raises
 * `NotLocal`, which a caller can catch and send to the server — the same shape as the engine's
 * own refusal, one level up.
 *
 * ⚠ THE JOURNAL IS THE UNDO. Every edit's `inverse` goes to OPFS as it happens, and undo replays
 * it: the OLD TAGS, never an inverse transform. `edits.do_transform` records why — rotating by
 * -θ after +θ moves a HATCH's pattern origin, and 61 hatches on a real client plan came back
 * with a different pattern base point.
 */
import { baseDocument } from "./base";
import { localEngine, LocalEngine, LocalEngineError } from "./engine";
import { appendJournal, clearJournal, readJournal, saveRows, type JournalEntry } from "./opfs";

/** Where the undo journal lives.
 *
 * ⚠⚠ **AN INTERFACE, BECAUSE OPFS IS ONE IMPLEMENTATION AND NOT THE ONLY ONE THAT MATTERS.** A
 * Tauri window has a real filesystem and should use it; a private browsing window has no OPFS at
 * all; and a test has neither and must still be able to prove that undo puts the old tags back.
 * The default is OPFS, which is the browser's answer — see `opfs.ts` for why not localStorage.
 */
export interface JournalStore {
  append(key: string, e: JournalEntry): Promise<boolean>;
  read(key: string): Promise<JournalEntry[]>;
  clear(key: string): Promise<void>;
  rows(key: string, name: string, rows: string, journalAt: number): Promise<boolean>;
}

export const OPFS_JOURNAL: JournalStore = {
  append: appendJournal,
  read: readJournal,
  clear: clearJournal,
  rows: saveRows,
};

/** **The journal in memory, written to OPFS BEHIND the edit.**
 *
 * ⚠⚠⚠ **`createWritable({keepExistingData: true})` COPIES THE WHOLE FILE BEFORE EVERY APPEND.**
 * That is the specified behaviour — the write goes to a swap file seeded from the original — so
 * appending one line to the undo journal costs O(journal), and a session that has been drafting
 * for an hour pays more for its next edit than for its first. Measured through the real studio on
 * drawing 10919: `editDrawing` took **27–37 ms** for a draw while the engine's own edit is 0.5 ms
 * and its display patch 1.0 (`qa/local_adapter.py --time`). The harness had never seen it,
 * because the harness uses `memoryJournal`.
 *
 * ⇒ The list in memory is what UNDO reads; OPFS is for crash recovery and nothing else waits for
 * it. Entries queue on one chain and are written in BATCHES, so a burst of edits costs one copy
 * rather than one each.
 *
 * ⚠⚠ **A FAILED WRITE IS STILL REPORTED** — `opfs.ts`'s own rule: an autosave a drafter believes
 * in and that is not there is worse than one they know they do not have. It cannot be reported by
 * returning false any more, because nobody is waiting, so `onFail` is called instead and the
 * studio says it out loud.
 * ⚠ `read` prefers memory and falls back to the inner store, which is what makes a RELOAD work:
 * a fresh tab has no memory and every entry is on disk.
 * ⚠ `clear` and `rows` go on the SAME chain. Clearing while an append is still queued would put
 * the cleared entry back.
 */
export function writeBehind(inner: JournalStore, onFail?: (why: string) => void): JournalStore {
  const mem = new Map<string, JournalEntry[]>();
  const queued = new Map<string, JournalEntry[]>();
  let chain: Promise<unknown> = Promise.resolve();
  let told = false;
  const fail = (why: string) => {
    if (told) return;
    told = true;
    onFail?.(why);
  };
  const after = (f: () => Promise<unknown>) => {
    chain = chain.then(f).catch((e) => fail(String((e as Error)?.message || e)));
    return chain;
  };
  const drain = (key: string) => after(async () => {
    const batch = queued.get(key);
    if (!batch || !batch.length) return;
    queued.set(key, []);
    for (const e of batch) {
      if (!await inner.append(key, e)) {
        fail("the browser would not write the autosave journal");
        return;
      }
    }
  });
  return {
    async append(key, e) {
      (mem.get(key) ?? mem.set(key, []).get(key)!).push(e);
      (queued.get(key) ?? queued.set(key, []).get(key)!).push(e);
      void drain(key);
      return true;
    },
    async read(key) {
      const got = mem.get(key);
      if (got) return [...got];
      const disk = await inner.read(key);
      mem.set(key, [...disk]);
      return disk;
    },
    async clear(key) {
      mem.set(key, []);
      queued.set(key, []);
      await after(() => inner.clear(key));
    },
    async rows(key, name, rows, journalAt) {
      // ⚠ The rows are the autosave's BASE and the journal is replayed onto them, so this has to
      // land in order with the appends around it — hence the same chain, and hence awaited: it
      // happens once at open, not per edit.
      mem.set(key, []);
      queued.set(key, []);
      await after(() => inner.rows(key, name, rows, journalAt));
      return true;
    },
  };
}

/** A journal that lives only as long as the tab does. ⚠ For a window with no OPFS and for tests
 *  — NOT a fallback anybody should be given silently: a drafter who believes they have autosave
 *  and does not is worse off than one who knows they do not. */
export function memoryJournal(): JournalStore {
  const log = new Map<string, JournalEntry[]>();
  return {
    async append(key, e) { (log.get(key) ?? log.set(key, []).get(key)!).push(e); return true; },
    async read(key) { return [...(log.get(key) ?? [])]; },
    async clear(key) { log.delete(key); },
    async rows() { return false; },
  };
}

/** `display.FLATTEN` — how finely a curve is flattened, in drawing units. ⚠ NOT a preference:
 *  the server bakes it into every cached display list, and a browser building a different one
 *  would draw the same drawing with different curves. */
export const FLATTEN = 0.35;

/** `dxfio.HANDLE_SHIFT` — where a replayed handle lands. ⚠ Same reason: it is part of what the
 *  display list MEANS, not a tuning knob. */
export const HANDLE_SHIFT = 0x400000;

/** Thrown for what local mode does not answer. ⚠ Its own type so a caller can fall back to the
 *  server without parsing a message. */
export class NotLocal extends Error {
  readonly what: string;
  constructor(what: string) {
    super(`${what} is the server's — local mode does not answer it`);
    this.name = "NotLocal";
    this.what = what;
  }
}

/** What `store.run` hands back — `ops::apply_json`'s document. */
export interface Applied {
  report: Record<string, unknown>;
  inverse: { op: "tags" | "restore"; rows: unknown[] };
  deletes: string[];
  creates: Array<{
    handle: string; kind: string; space: number | null; block: number | null;
    layer: number | null; columns: unknown; tags: unknown; data: unknown;
  }>;
  writes: Array<{ handle: string; tags: unknown; data: unknown }>;
  deferred?: string[];
}

/** What a drawing needs to be opened locally. */
export interface OpenSpec {
  /** `display_rs.store_input`'s JSON — every row, as the engine reads them. */
  rows: string;
  /** For the autosave key and the title bar. */
  name: string;
  /** `$ACADVER`, e.g. `AC1032`. ⚠ Picks the base document, so it must be the drawing's own. */
  version: string;
  /** Whether the drawing brought its own symbol table — see `base.ts`: this is NOT a preference. */
  hasTables: boolean;
  /** `codepage_of(dwg)` — the codec the drawing says its legacy bytes are in. */
  codepage?: string;
  /** The display context (`display_rs.ctx_of`), as JSON.
   *
   * ⚠ OPTIONAL now: the engine builds its own from the store (`engine::ctx`), held to Python's
   * over the whole corpus by `qa/ctx_parity.py`. A drawing that came from the SERVER passes the
   * server's, because only the server knows its RASTERS — they are files, keyed by IMAGEDEF
   * handle, and a drawing opened from a disk has none. */
  ctx?: string;
  /** Where the undo journal goes. ⚠ OPFS by default — see `JournalStore`. */
  journal?: JournalStore;
  /** The file this drawing was opened FROM, where the browser gives one.
   *
   * ⚠⚠ **A HANDLE IS THE DIFFERENCE BETWEEN "Save" AND "Save a copy".** Without it the only
   * honest offer is a download, which lands wherever the browser puts downloads and never
   * overwrites what the drafter opened — see `files.ts`. Kept here rather than in the registry
   * because it belongs to the drawing, not to the id. */
  handle?: FileSystemFileHandle | null;
}

export class LocalDrawing {
  readonly name: string;
  readonly key: string;
  private readonly engine: LocalEngine;
  private ctx: string;
  private readonly codepage: string;
  private base = "";
  private readonly log: JournalStore;

  /** What the last `edit` spent, split — see the note in `edit`. ⚠ Static and overwritten: it is a
   *  probe for `qa/`, not state anything in the product reads. */
  static lastEdit: Record<string, number | string> | null = null;

  /** The delta the last `edit` brought back with it, or `undefined` where the engine declined to
   *  patch incrementally. ⚠ Consumed by `displayPatch`, which is now a READ of this rather than
   *  a second call into the module — see `edit`. */
  private lastDelta: unknown | undefined;
  /** ⚠ `null` where the drawing came from the server or from a browser with no File System
   *  Access — the caller offers a download instead, and says so. */
  readonly handle: FileSystemFileHandle | null;
  /** How many journal entries have been undone and can be redone. ⚠ Held here, not in OPFS: a
   *  redo stack does not survive a crash in AutoCAD either, and pretending otherwise would mean
   *  writing a second file on every undo. */
  private redoable: JournalEntry[] = [];

  private constructor(engine: LocalEngine, spec: OpenSpec, key: string) {
    this.engine = engine;
    this.name = spec.name;
    this.key = key;
    this.ctx = spec.ctx ?? "";
    this.codepage = spec.codepage || "cp1252";
    // ⚠ OPFS wrapped in `writeBehind` — see its note. A caller that hands its own store (the
    // tests, `memoryJournal`) gets exactly that one, because wrapping a store whose whole point
    // is to be synchronous and inspectable would make every test race its own journal.
    this.log = spec.journal ?? writeBehind(OPFS_JOURNAL, (why) => {
      // ⚠ Once, loudly, on the object the studio already watches. Silence here is the one thing
      // `opfs.ts` says must not happen.
      try {
        const w = window as unknown as Record<string, unknown>;
        w.__pdcadAutosaveFailed = why;
        console.error(`pdcad: ${why} — your edits are in this tab only until you save.`);
      } catch { /* a report must never be the reason an edit fails */ }
    });
    this.handle = spec.handle ?? null;
  }

  /** Open a drawing into the browser engine. ⚠ The base document is fetched FIRST: a store that
   *  is open with no base can answer every read and cannot draw, which is the worst of both. */
  static async open(spec: OpenSpec, engine: LocalEngine = localEngine()): Promise<LocalDrawing> {
    // ⚠⚠ **A SECOND BIG DRAWING NEEDS A NEW MODULE.** Reading a 48 MB .dwg leaves ~2.8 GB of
    // wasm heap that the allocator will not hand back, and wasm32's address space is 4 GB — so
    // the next one dies inside the reader while opening perfectly on its own. Measured in
    // `qa/wasm_dwg.py`. ⚠ A GB is the threshold, not a byte count: recycling a warm module for
    // a small drawing would cost a re-instantiation for nothing.
    if (engine.heapBytes() > 1_000_000_000) engine.recycle();
    // ⚠⚠ **WHERE TAKING A DRAWING LOCAL SPENDS ITS TIME.** Measured 2026-09-19: 29.9 MB of rows
    // (52,306 objects) is 733 ms — `store.open` 603 of it, the journal 120, the base document 2.
    // 40.0 MB (93,666) is 748 ms. It is LINEAR in the bytes and the engine is nearly all of it.
    // ⚠⚠⚠ **AND A DRAWING TOO BIG FOR A TAB NEVER REACHES HERE AT ALL** — the rows endpoint
    // answers 413 with a sentence, in 373 ms, and the studio prints it: *"…is about 949 MB of
    // rows, and a browser tab can hold about 300 MB. It opens and edits on the server as usual."*
    // A probe that waited for the success message read that as a seven-minute hang and it was
    // nothing of the kind. ⚠ The DESKTOP app is always local mode, so those two client drawings
    // (1,021,505 and 880,331 objects) are server-only in the browser and open from DISK in the
    // desktop — a different path, through the engine's own reader, and not yet measured.
    const split: Record<string, number> = {};
    (window as unknown as Record<string, unknown>).__pdcadLocalSplit = split;
    const t0 = performance.now();
    const d = new LocalDrawing(engine, spec, spec.name);
    d.base = await baseDocument(spec.version, spec.hasTables === false);
    split.base = performance.now() - t0;
    split.rowBytes = spec.rows.length;
    const t1 = performance.now();
    await engine.must("store.open", [spec.rows]);
    split.storeOpen = performance.now() - t1;
    const t2 = performance.now();
    // ⚠ the store's OWN context where the caller has none — which is the case for a .dwg opened
    // from a disk. `FLATTEN` is `display.FLATTEN` and `HANDLE_SHIFT` is `dxfio.HANDLE_SHIFT`;
    // both are constants of the format, not settings, and `qa/ctx_parity.py` holds the whole
    // document to Python's.
    if (!d.ctx) {
      d.ctx = await engine.must("store.ctx", [String(FLATTEN), String(HANDLE_SHIFT), "{}"]);
    }
    // ⚠ AFTER the ctx is resolved, or the module is handed an empty one and every edit comes back
    // with no delta — which reads as "the engine declined to patch" and refetches the whole list.
    await d.pushCtx();
    split.ctx = performance.now() - t2;
    const t3 = performance.now();
    // ⚠ the autosave is written at OPEN, not only on edit: a tab killed before the first command
    // should still find the drawing it was showing, and this is the only moment the rows are
    // known to be exactly the file's.
    await d.log.rows(d.key, d.name, spec.rows, 0);
    split.journal = performance.now() - t3;
    split.total = performance.now() - t0;
    return d;
  }

  /** A drawing the engine has ALREADY opened — `dwg.open` reads, imports and opens it inside the
   *  module in one call, so there is no `rows` document on this side to hand back in.
   *
   * ⚠ Same construction as `open`, minus `store.open` and minus the autosave: there is nothing
   * here to autosave, because the rows never crossed. A file opened from disk is protected by
   * the file, and the journal protects the edits. */
  static async attach(spec: Omit<OpenSpec, "rows" | "ctx"> & { ctx?: string },
                      engine: LocalEngine = localEngine()): Promise<LocalDrawing> {
    const d = new LocalDrawing(engine, { ...spec, rows: "" } as OpenSpec, spec.name);
    d.base = await baseDocument(spec.version, spec.hasTables === false);
    if (!d.ctx) {
      d.ctx = await engine.must("store.ctx", [String(FLATTEN), String(HANDLE_SHIFT), "{}"]);
    }
    await d.pushCtx();                            // ⚠ same reason as `open` — see `store.setctx`
    return d;
  }

  async close(): Promise<void> {
    await this.engine.close();
  }

  // ── the reads, one engine call each ──────────────────────────────────────────────────────
  layouts(): Promise<{ layouts: unknown[] }> { return this.engine.json("store.layouts"); }
  layers(): Promise<unknown[]> { return this.engine.json("store.layers"); }
  blocks(): Promise<unknown[]> { return this.engine.json("store.blocks"); }
  groups(): Promise<{ groups: unknown[] }> { return this.engine.json("store.groups"); }
  fields(): Promise<unknown> { return this.engine.json("store.fields"); }
  dimStyles(): Promise<unknown> { return this.engine.json("store.dimstyles"); }
  textStyles(): Promise<unknown> { return this.engine.json("store.textstyles"); }
  blockAtts(block = ""): Promise<unknown> { return this.engine.json("store.blockatts", [block]); }
  blockDef(name: string): Promise<unknown> { return this.engine.json("store.block_def", [name]); }
  text(handle: string): Promise<unknown> { return this.engine.json("store.text", [handle]); }

  /** The whole drawing as DXF text — what a SAVE writes (`engine::todxf`).
   *
   * ⚠⚠ **THE ROWS ARE THE DRAWING, INCLUDING EVERY EDIT MADE IN THIS TAB.** `store.apply`
   * rewrites an entity's TAGS, and this writes the tags, so a save carries the edits without a
   * second authoring step. Held to `dxfio.export_dxf` over forty drawings by
   * `qa/store_to_dxf_parity.py` — the same entity census and the same picture, and the ODA File
   * Converter accepts what it writes.
   * ⚠ A DXF, whatever the file was opened as: this app reads DWG and delivers DXF
   * (`dwgio.py`), and `suggestName` renames the suffix to match rather than lie about it. */
  toDxf(): Promise<string> { return this.engine.must("store.to_dxf"); }

  /** The drawing as a .dwg — the file the drafter handed over, given back as itself.
   *
   * ⚠⚠ **A DXF IS NOT A DWG TO THE PERSON RECEIVING IT.** Every reader takes one, and a client
   * who sent a .dwg and gets a .dxf back has to explain it to whoever they forward it to. The
   * writer is `pdcad_core::todwg` and the ODA File Converter opens what it makes
   * (`qa/rust_dwgwrite.py`); 99% of a 19 MB client drawing survives the round trip, and what
   * does not is COUNTED — the report says so, by kind. */
  toDwg(): Promise<{ report: unknown; bytes: Uint8Array }> {
    return this.engine.callForBytes("store.to_dwg");
  }

  /** ⚠ The user variables go IN, because `sysvars` merges the drawing's header with whatever the
   *  drafter has set — the server reads them from `UserVar` and a browser holds its own. */
  sysvars(user: Record<string, unknown> = {}): Promise<unknown> {
    return this.engine.json("store.sysvars", [JSON.stringify(user)]);
  }

  /** The display list — what `GET /display/` answers, built here instead.
   *
   * ⚠ The payload is JSON TEXT (`Built::payload` is `to_json`), so this is a parse and not a
   * decode. The SIDECAR is the server's own business (`display.patch_caches`) and is dropped.
   */
  async display(space = "Model"): Promise<unknown> {
    const two = await this.engine.json<[string, string]>(
      "store.display", [this.ctx, this.codepage, this.base, space]);
    // ⚠⚠ **THE SIDECAR STAYS IN THE MODULE.** It used to be carried out here, held in this
    // object and handed back on every patch — a 5.3 MB string across `postMessage` twice per
    // edit, for a document only the engine ever reads. `Store::held` keeps it now; all this has
    // to remember is which space the picture is of.
    this.shown = space;
    return JSON.parse(two[0]);
  }

  /** The space `display` was last asked for — the one the studio is showing, and so the one an
   *  edit has to redraw. ⚠ Not a parameter of `edit`: the studio's space lives in `Studio.tsx`
   *  and `api.ts` routes by drawing id alone, so asking for it at the edit would mean threading
   *  it through every call site — the exact trap `api.ts`'s own note describes. */
  private shown = "Model";

  /** **An incremental redraw with no server** — `display.patch` in the tab.
   *
   * ⚠⚠⚠ Measured before it existed, with `qa/local_adapter.py --time` on drawing 10919
   * (57,385 entities): the engine's EDIT is 5.8 ms — BricsCAD's own MOVE is 3 — and the redraw
   * was **716.8 ms**, because the studio had no delta and refetched the whole list.
   *
   * ⚠ `undefined` means the engine declined and the caller must rebuild, which is the server's
   * contract too. Returning a guess would draw a plausible, wrong picture.
   * ⚠ The sidecar is only replaced when the splice SUCCEEDS: a failed patch must leave the next
   * one splicing against the document the picture on screen was actually built from.
   */
  /** The delta for the rows an edit just changed.
   *
   * ⚠⚠ **AFTER AN `edit` THIS IS A READ, NOT A CALL.** `store.edit` brings the delta back with
   * the answer, because the handles to redraw are only known once the command has run — a
   * separate call had to wait for the answer first, and that round trip cost more than the
   * redraw. Called on its own (a refresh with no edit behind it) it still asks the module.
   */
  async displayPatch(handles: string[]): Promise<unknown | undefined> {
    if (this.lastDelta !== undefined) {
      const d = this.lastDelta;
      this.lastDelta = undefined;                 // ⚠ once: a second read is a different edit
      return d;
    }
    if (!handles.length) return undefined;
    try {
      const delta = await this.engine.must(
        "store.display_patch", [this.ctx, this.codepage, this.base, JSON.stringify(handles)]);
      return JSON.parse(delta);
    } catch {
      return undefined;
    }
  }

  /** What `displayPatch` will redraw into — the space the studio is showing. */
  shownSpace(): string {
    return this.shown;
  }

  /** Hand the module the three values `display_patch` needs, so an edit does not carry them.
   *
   * ⚠ Called at open and after anything that changes the context — the shown space, a base
   * document swap. Cheap and idempotent: three strings into a thread-local.
   */
  async pushCtx(): Promise<void> {
    await this.engine.must("store.setctx", [this.ctx, this.codepage, this.base]);
  }

  // ── the write path ───────────────────────────────────────────────────────────────────────

  /** Run a command and keep it. ⚠ TWO engine calls on purpose: `store.run` computes and performs
   *  NOTHING, `store.apply` plays the answer into the rows. A caller that wants a preview runs
   *  without applying — which is what the drag handles will want. */
  async edit(op: string, handles: string[],
             params: Record<string, unknown> = {}): Promise<Applied> {
    // ⚠⚠⚠ **THE ENGINE'S ANSWER GOES BACK TO THE ENGINE AS THE TEXT IT CAME AS.** It used to be
    // parsed here and `JSON.stringify`d on the way in — and JavaScript has one number type, so
    // every `0.0` in it became `0`. The tags a browser edit stored then differed from the ones
    // the server stores for the same command, on values the command never touched:
    // `qa/local_adapter.py` caught `[30, 0.0]` written back as `[30, 0]`. The DXF a writer makes
    // of either is the same, and that is exactly why nothing had ever noticed.
    // ⚠ It is also one fewer parse and one fewer serialise of an answer that is megabytes on a
    // large selection.
    // ⚠⚠⚠ **ONE CALL, NOT THREE.** This was `store.run`, `store.apply` and then
    // `displayPatch` — three `postMessage` round trips for one command, and the main thread runs
    // a continuous rAF loop so each reply waits for a frame. Measured through the real studio on
    // drawing 10919: `store.run` spent **1.8-5 ms inside the module and 15-18 ms of wall time**.
    // The module was never the slow part; the crossing was, three times over.
    const t0 = performance.now();
    // ⚠ The ctx, the codepage and the base document are NOT arguments here — `store.setctx` set
    // them at open. The base document alone is 14-34 KB of DXF text and none of the three can
    // change while a drawing is open; carrying them per keystroke was 48 KB across `postMessage`
    // and into the module's memory for every edit.
    const raw = await this.engine.call(
      "store.edit", [op, JSON.stringify(handles), JSON.stringify(params)]);
    if (raw.rc !== 0) throw new LocalEngineError("store.edit", raw.text);
    const t1 = performance.now();
    const [applied, deltaText, deltaWhy] = JSON.parse(raw.text) as [string, string, string?];
    const got = JSON.parse(applied) as Applied;
    // ⚠ The delta rides along. `undefined` where the engine declined to patch incrementally — a
    // new layer, an image — and the caller refetches, exactly as it did when this was its own
    // call.
    this.lastDelta = deltaText ? JSON.parse(deltaText) : undefined;
    const t2 = performance.now();
    // ⚠⚠ **THE ENGINE'S OWN CLOCK BESIDE THE WALL CLOCK, BECAUSE THE GAP IS THE POINT.** `ms` is
    // what the module spent; the difference is `postMessage`, the structured clone of an answer
    // that is hundreds of kilobytes on a big selection, and this JSON parse. Two guesses at where
    // a 26 ms local draw went were wrong before this existed.
    LocalDrawing.lastEdit = {
      engineMs: raw.ms, wall: t1 - t0, parse: t2 - t1, bytes: raw.text.length,
      // ⚠ Why there is no delta, when there is none. A missing delta makes the studio refetch
      // the whole display list and rebuild the GPU scene, and the absence of a splice mark reads
      // exactly like a command that was simply fast — see the engine's own note.
      deltaWhy: deltaWhy || "",
    };
    this.redoable = [];                    // ⚠ a new command ends the redo chain, as everywhere
    await this.log.append(this.key, {
      op, handles, params, inverse: { ...got.inverse, made: got.creates.map((c) => c.handle) },
      at: Date.now(),
    } as JournalEntry);
    return got;
  }

  /** Undo the last command. `null` when there is nothing to undo, otherwise THE HANDLES IT
   *  TOUCHED.
   *
   *  ⚠⚠⚠ **IT USED TO RETURN A BARE `true` AND THAT COST 247 ms A PRESS.** With no handles, the
   *  studio has nothing to ask `displayPatch` for, so it refetched the whole display list from
   *  the engine — which hands back entirely fresh rows, which throws away `gpu/build.ts`'s fill
   *  cache, which re-triangulates all 4,934 fills. Measured on drawing 1158 in local mode:
   *  **scene build 309-376 ms, of which `fills` 243-247**, on the key a drafter presses most.
   *  Every other command has carried its touched handles since `editDrawingOf`; undo never did.
   */
  async undo(): Promise<string[] | null> {
    const log = await this.log.read(this.key);
    const last = log[log.length - 1];
    if (!last) return null;
    const touched = await this.replay(last, true);
    this.redoable.push(last);
    // ⚠ The journal is rewritten WITHOUT its last line. OPFS has no truncate-in-place that is
    // simpler than this, and an undone command that stayed in the file would be undone twice.
    await this.log.clear(this.key);
    for (const e of log.slice(0, -1)) await this.log.append(this.key, e);
    return touched;
  }

  /** Redo what undo took back — same contract as `undo`. */
  async redo(): Promise<string[] | null> {
    const next = this.redoable.pop();
    if (!next) return null;
    const touched = await this.replay(next, false);
    await this.log.append(this.key, next);
    return touched;
  }

  /** Put one journal entry back, either way round.
   *
   * ⚠⚠ **UNDO IS THE OLD TAGS, NOT AN INVERSE TRANSFORM**, and REDO is the command run again —
   * they are not symmetrical and cannot be. Rotating by -θ after +θ is not the inverse of
   * rotating by +θ: `edits.do_transform` measured 61 HATCHes coming back with a different
   * pattern base point, the boundary exact throughout.
   */
  private async replay(entry: JournalEntry, back: boolean): Promise<string[]> {
    if (!back) {
      // ⚠ A redo IS the command, so it goes through `edit` and leaves its own `lastDelta` —
      // `displayPatch` will read that and never reach the handles returned here.
      await this.edit(entry.op, entry.handles, entry.params as Record<string, unknown>);
      return entry.handles.map((h) => String(h).toUpperCase());
    }
    const inv = entry.inverse as {
      op: "tags" | "restore" | "header"; rows: Array<Record<string, unknown>>; made?: string[];
    };
    const doc: Record<string, unknown> = {};
    // ⚠⚠ **A HEADER CHANGE HAS NO ROW TO PUT BACK.** SETVAR writes a system variable — `$LTSCALE`
    // decides what every dashed line in the drawing looks like — so its inverse is neither
    // `tags` nor `restore`, and routing it through either would undo nothing while the journal
    // said it had. The engine emits the prior value, or a `remove` when the drawing never
    // carried the variable: writing its default instead would CHANGE the file.
    if (inv.op === "header") {
      doc.header = inv.rows;
      await this.engine.must("store.apply", [JSON.stringify(doc)]);
      // ⚠ No handles: a system variable changes how the WHOLE drawing draws, so the caller has
      // nothing to patch and refetches, which is the honest answer here.
      return [];
    }
    if (inv.op === "restore") {
      doc.restores = inv.rows;
    } else {
      // ⚠ The inverse carries the TAGS and not the `data` projection — the journal is the row's
      // own bytes. So the projection is recomputed here, by the same engine that computes it on
      // the server (`reads::projection`), rather than left stale: a row whose tags say one thing
      // and whose `data` says another is a drawing that draws in two places at once.
      const writes = [];
      for (const r of inv.rows) {
        const tags = JSON.stringify(r.t);
        const a = await this.engine.call("projection", [tags]);
        writes.push({ handle: r.h, tags: r.t, data: a.rc === 0 ? JSON.parse(a.text) : null });
      }
      doc.writes = writes;
    }
    // ⚠ what the command CREATED goes away again — a copy's undo is a delete, never a restore
    if (inv.made && inv.made.length) doc.deletes = inv.made;
    await this.engine.must("store.apply", [JSON.stringify(doc)]);
    // ⚠ Every handle the undo moved, so the caller can ask for a display patch over exactly
    // those. A restored row names itself in `handle`; a rewritten one in `h`; a deleted one is
    // in `made`. Missing any of them leaves that object drawn as it was.
    const touched = new Set<string>();
    for (const r of inv.rows || []) {
      const h = (r as Record<string, unknown>).h ?? (r as Record<string, unknown>).handle;
      if (h) touched.add(String(h).toUpperCase());
    }
    for (const h of inv.made || []) touched.add(String(h).toUpperCase());
    return [...touched];
  }

  /** How many commands are on the journal — what an "unsaved changes" prompt asks. */
  async pending(): Promise<number> {
    return (await this.log.read(this.key)).length;
  }

  // ── what local mode does not answer ──────────────────────────────────────────────────────
  grips(_handle: string): Promise<never> { return Promise.reject(new NotLocal("grips")); }
  measure(): Promise<never> { return Promise.reject(new NotLocal("measure")); }
  attachImage(): Promise<never> { return Promise.reject(new NotLocal("attach_image")); }
  parametric(): Promise<never> { return Promise.reject(new NotLocal("parametric")); }
}

/** Is the message a refusal the caller should send to the server? ⚠ Both doors: the engine's own
 *  `rc` 1 and this module's `NotLocal`. */
export function shouldAskTheServer(e: unknown): boolean {
  return e instanceof NotLocal || e instanceof LocalEngineError;
}
